/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import {ExdsButton, ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from '@exds/core';
import {ExdsTextInput} from "@exds/controls";
import StartOverModal from "../../StartOverModal/StartOverModal";
import MissingAccessCodeExternalModal from "../MissingAccessCodeExternalModal/MissingAccessCodeExternalModal";
import {apiPost} from '../../../service/service';
import {doEncrypt} from '../../E2ee/E2ee';
import AppContext from '../../../AppContext';
import {useNavigate} from 'react-router-dom';
import {
  ERROR_ALL_FIELDS_REQUIRED,
  NOT_ALLOWED_TO_ONBOARD_TODAY, ONBOARDED_USER,
  VALIDATE_IDENTITY_ERROR_CONTENT
} from '../../../constants/constants';
import Error from '../../Error/Error';
import {TuxLoadingIndicator} from '@tuxedo/react-component-library';
import {addPageLoadEvent} from "../../AdobeAnalytics/AdobeAnalytics";

export default function AccessCodeExternal({pageName}) {
  const {sidContext, phoneNumberContext, hashContext, transactionIdContext, firstNameContext, lastNameContext, mppUrlContext, expirationTimeContext, isSoftTokenEnabledContext,isSmartLogonEnabledContext, loadAA} = useContext(AppContext);
  const setHash = hashContext.at(1);
  const sid = sidContext.at(0);
  const setSid = sidContext.at(1);
  const setPhoneNumber = phoneNumberContext.at(1);
  const setTransactionId = transactionIdContext.at(1);
  const setFirstName = firstNameContext.at(1);
  const setLastName = lastNameContext.at(1);
  const mppUrl = mppUrlContext.at(0);
  const setExpirationTime = expirationTimeContext.at(1);
  const setIsSoftTokenEnabled = isSoftTokenEnabledContext.at(1);
  const setIsSmartLogonEnabled = isSmartLogonEnabledContext.at(1);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const [isAccessCodeModalOpen, setIsAccessCodeModalOpen] = useState(false);
  const [accessCodePrimary, setAccessCodePrimary] = useState('');
  const [accessCodeSecondary, setAccessCodeSecondary] = useState('');
  const [hasSidError, setHasSidError] = useState(false);
  const [hasAccessCodePrimaryError, setHasAccessCodePrimaryError] = useState(false);
  const [hasAccessCodeSecondaryError, setHasAccessCodeSecondaryError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const ENTER_SID_MICROCOPY_TEXT = 'Enter your Standard ID (SID) that was sent to you upon hire';
  const ENTER_PRIMARY_ACCESS_CODE_MICROCOPY_TEXT = 'Enter the temporary access code that was provided by your onboarding facilitator';
  const ENTER_SECONDARY_ACCESS_CODE_MICROCOPY_TEXT = 'Enter the access code sent to your mobile phone';
  const navigate = useNavigate();
  const [displayError, setDisplayError] = useState('');
  const [buttonLink, setButtonLink] = useState('');
  const [buttonTitle, setButtonTitle] = useState('');
  const [errorContent, setErrorContent] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [windowDisplayTarget, setWindowDisplayTarget] = useState('_self');
  const [windowDimensions, setWindowDimensions] = useState('');

  useEffect(() => {
    if(loadAA) {
      addPageLoadEvent(pageName);
    }
  }, [loadAA]); // eslint-disable-line react-hooks/exhaustive-deps

  async function getVerifyOtacRequestBody() {
    const ePrimary = await doEncrypt(accessCodePrimary);
    const eSecondary = await doEncrypt(accessCodeSecondary);
    const result = {
      sid,
      primary: ePrimary,
      secondary: eSecondary,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    return result;
  }
  function setErrorDisplayDetails(
    correlationId,
    buttonLink,
    buttonTitle ,
    content,
    target,
    dimensions){
    setCorrelationId(correlationId);
    setButtonLink(buttonLink);
    setButtonTitle(buttonTitle);
    setErrorContent(content);
    setWindowDisplayTarget(!target? '_self' : target);
    setWindowDimensions(!dimensions ? '' : dimensions );
    setDisplayError(true);
  }

  function handleErrorResponse(errorCode, correlationId) {
    switch (errorCode) {
      case "ACTIVE":
        setErrorDisplayDetails(correlationId,mppUrl,ONBOARDED_USER,'_blank','width=800,height=600');
        break;
      case "NOT_TODAY":
        setErrorDisplayDetails(correlationId,null,null,NOT_ALLOWED_TO_ONBOARD_TODAY);
        break;
      case "SYS_ERROR":
      default:
        setErrorDisplayDetails(correlationId,'/validateIdentity','Try again', VALIDATE_IDENTITY_ERROR_CONTENT);
        break;
    }
  }
  function handleSuccessfulValidation(response) {
    setFirstName(response.data.firstName);
    setLastName(response.data.lastName);
    setPhoneNumber(response.data.phoneNumber);
    setTransactionId(response.data.transactionId);
    setExpirationTime(response.data.expirationTime);
    setIsSmartLogonEnabled(response.data.smartLogonEnabled);
    setIsSoftTokenEnabled(response.data.softTokenEnabled);
    setHash(response.data.hash);
    navigate('/createPassword');
  }
  async function processSubmit(payload) {
    setFormSubmitted(true);
    try {
      const response = await apiPost('/api/validateUserOtacs', payload);
      setFormSubmitted(false);
      console.info("api/validateUserOtacs response",response);
      if (response.status === 200) {
        if(!response.data.errorCode)
          handleSuccessfulValidation(response);
        else
          handleErrorResponse(response.data.errorCode,response.correlationId);
      } else {
        setErrorDisplayDetails(response.correlationId,'/validateIdentity','Try again', VALIDATE_IDENTITY_ERROR_CONTENT);
      }
    } catch (err) {
      setErrorDisplayDetails(err.correlationId,'/validateIdentity','Try again', VALIDATE_IDENTITY_ERROR_CONTENT);
    }

  }

  useEffect(() => {
    document.title = 'New Hire Portal - Validate Identity_Access Code';
  }, []);

  useEffect(() => {
    if (formSubmitted) {
      const errors = document.querySelectorAll('[aria-describedby*="label-error-text"]');
      errors?.item(0)?.focus();
    }
  }, [formSubmitted]);

  const submit = async (e) => {
    e.preventDefault();
    const sidError = !sid || sid.trim() === '';
    const primaryError = !accessCodePrimary || accessCodePrimary.trim() === '';
    const secondaryError = !accessCodeSecondary || accessCodeSecondary.trim() === '';
    setHasSidError(sidError);
    setHasAccessCodePrimaryError(primaryError);
    setHasAccessCodeSecondaryError(secondaryError);
    if (!sidError && !primaryError && !secondaryError){
      const requestPayload = await getVerifyOtacRequestBody();
      processSubmit(requestPayload);
    }
  };

  return (
    <>
      <form onSubmit={submit} noValidate>

        {(formSubmitted || displayError) &&
          <ExdsGrid
            container
            justifyContent="center"
            className="access-code-container"
          >
            {formSubmitted && <div className="loading-spinner"><TuxLoadingIndicator/></div>}
            {displayError && <Error
              content={errorContent}
              buttonTitle={buttonTitle}
              buttonLink={buttonLink}
              correlationId={correlationId}
              windowOpenTarget={ windowDisplayTarget}
              windowDimensions= {windowDimensions}
              pageName={pageName}
            />}
          </ExdsGrid>}
        {!formSubmitted && !displayError && <ExdsGrid
          container
          justifyContent="center"
          className="access-code-container"
        >
          <ExdsLayout
            alignItems="flex-start"
            display="flex"
            flexDirection="column"
            gap="1.5rem"
          >
            <ExdsText
              primitive="p"
              textStyle="bodyLargeHeavier"
              className="description"
            >
              First we need to validate your identity to ensure you have the appropriate access. To do this, your onboarding facilitator
              will provide you with a primary (verbal) access code while sending a secondary temporary access code to your mobile phone.&nbsp;
              <b>This will be the mobile phone number you registered during your application process.</b>
            </ExdsText>
            <div className="access-code-input">
              <ExdsSpacing margin="5px 0px 0px 0px">
                <ExdsTextInput
                  id="input-sid"
                  value={sid}
                  onChange={(e) => {
                    setSid(e.target.value);
                    setHasSidError(false);
                  }}
                  labelText="Standard ID (SID)"
                  microcopyText={!hasSidError ? ENTER_SID_MICROCOPY_TEXT : ''}
                  errorText={hasSidError ? ERROR_ALL_FIELDS_REQUIRED : ''}
                  required
                />
              </ExdsSpacing>

              <ExdsSpacing margin="20px 0px 0px 0px">
                <ExdsTextInput
                  id="input-primary-access-code"
                  value={accessCodePrimary}
                  onChange={(e) => {
                    setAccessCodePrimary(e.target.value);
                    setHasAccessCodePrimaryError(false);
                  }}
                  labelText="Primary (Verbal) Access Code"
                  microcopyText={!hasAccessCodePrimaryError ? ENTER_PRIMARY_ACCESS_CODE_MICROCOPY_TEXT : ''}
                  errorText={hasAccessCodePrimaryError ? ERROR_ALL_FIELDS_REQUIRED : ''}
                  required
                />
              </ExdsSpacing>

              <ExdsSpacing margin="20px 0px 0px 0px">
                <ExdsTextInput
                  id="input-secondary-access-code"
                  value={accessCodeSecondary}
                  onChange={(e) => {
                    setAccessCodeSecondary(e.target.value);
                    setHasAccessCodeSecondaryError(false);
                  }}
                  labelText="Secondary Access Code"
                  microcopyText={!hasAccessCodeSecondaryError ? ENTER_SECONDARY_ACCESS_CODE_MICROCOPY_TEXT : ''}
                  errorText={hasAccessCodeSecondaryError ? ERROR_ALL_FIELDS_REQUIRED : ''}
                  required
                />
              </ExdsSpacing>
            </div>
            <ExdsButton
              text="I haven't received my code"
              variant="link"
              type="button"
              className="access-code-button"
              onClick={() => setIsAccessCodeModalOpen(true)}
            />
            <div className="buttons-container">
              <ExdsButton
                text="Start over"
                variant="secondary"
                type="button"
                className="action-button"
                onClick={() => setIsStartOverModalOpen(true)}
              />
              <ExdsButton
                text="Submit"
                type="submit"
                className="action-button"
                onClick={submit}
              />
            </div>
          </ExdsLayout>
          <MissingAccessCodeExternalModal isAccessCodeModalOpen={isAccessCodeModalOpen} setIsAccessCodeModalOpen={setIsAccessCodeModalOpen}/>
          <StartOverModal isStartOverModalOpen={isStartOverModalOpen} setIsStartOverModalOpen={setIsStartOverModalOpen}/>
        </ExdsGrid>}
      </form>
    </>
  );
}
